import {useCallback, useEffect, useMemo, useState} from "react"
import {
    useEmpresasList,
    normalizeDoc,
    defaultGetLabel,
} from "./useEmpresasList"
import {
    normalizeAutocompleteText,
    normalizeCompanySearch,
} from "src/v2/utils/autocomplete/filter-autocomplete-options"

const defaultFieldState = {
    value: "",
    inputValue: "",
    invalid: false,
    pendingResolution: false,
}

/**
 * Controlador de autocomplete para empresas.
 * Mantém estado por campo e expõe handlers compatíveis com InputAutoComplete.
 */
export const useEmpresasAutocompleteController = (reloadKey = 0) => {
    const {options, loading} = useEmpresasList(true, reloadKey)
    const [stateMap, setStateMap] = useState({})

    const getState = useCallback(
        (key) => stateMap[key] || defaultFieldState,
        [stateMap]
    )

    const getLabel = useCallback((option) => defaultGetLabel(option), [])

    const findOption = useCallback(
        (rawValue) => {
            const target = normalizeDoc(
                typeof rawValue === "string"
                    ? rawValue
                    : rawValue?.value || rawValue
            )

            if (!target) return null

            return options.find(
                (option) => normalizeDoc(option.value) === target
            ) || null
        },
        [options]
    )

    const findByLooseMatch = useCallback(
        (rawValue) => {
            if (!options?.length) return null

            const raw = typeof rawValue === "object"
                ? rawValue?.value || rawValue?.label || ""
                : rawValue

            const document = normalizeDoc(raw)
            const normalizedText = normalizeAutocompleteText(raw)

            if (!normalizedText && !document) return null

            return options.find((option) => {
                const optionDocument = normalizeDoc(option.value)
                const optionLabel = normalizeAutocompleteText(option.label)

                const documentMatch = Boolean(
                    document && optionDocument === document
                )

                const labelMatch = Boolean(
                    normalizedText &&
                    optionLabel.includes(normalizedText)
                )

                return documentMatch || labelMatch
            }) || null
        },
        [options]
    )

    const isValid = useCallback(
        (rawValue) => Boolean(findByLooseMatch(rawValue)),
        [findByLooseMatch]
    )

    const updateState = useCallback((key, updater) => {
        setStateMap((previousState) => {
            const currentState = previousState[key] || defaultFieldState
            const nextState = updater(currentState)

            if (
                currentState.value === nextState.value &&
                currentState.inputValue === nextState.inputValue &&
                currentState.invalid === nextState.invalid &&
                currentState.pendingResolution === nextState.pendingResolution
            ) {
                return previousState
            }

            return {...previousState, [key]: nextState}
        })
    }, [])

    const handleChange = useCallback(
        (key, _event, newValue, reason) => {
            void _event

            updateState(key, (currentState) => {
                const normalized = normalizeDoc(
                    typeof newValue === "string"
                        ? newValue
                        : newValue?.value || newValue
                )

                if (reason === "clear" || (!normalized && !newValue)) {
                    return defaultFieldState
                }

                const matchedOption = findOption(normalized)
                const label = matchedOption
                    ? getLabel(matchedOption)
                    : getLabel(newValue) || normalized

                if (reason === "selectOption") {
                    return {
                        value: normalized,
                        inputValue: label,
                        invalid: false,
                        pendingResolution: false,
                    }
                }

                return {
                    value: normalized,
                    inputValue: label,
                    invalid: currentState.invalid,
                    pendingResolution: false,
                }
            })
        },
        [findOption, getLabel, updateState]
    )

    const handleInputChange = useCallback(
        (key, _event, newInput) => {
            void _event

            updateState(key, () => {
                if (!newInput?.trim?.().length) return defaultFieldState

                return {
                    value: normalizeDoc(newInput),
                    inputValue: newInput,
                    invalid: false,
                    pendingResolution: false,
                }
            })
        },
        [updateState]
    )

    const handleBlur = useCallback(
        (key) => {
            updateState(key, (currentState) => {
                const raw = currentState.inputValue ||
                    currentState.value ||
                    ""

                if (!raw.trim().length) return defaultFieldState

                if (!options?.length) {
                    return {
                        value: normalizeDoc(raw),
                        inputValue: currentState.inputValue || raw,
                        invalid: false,
                        pendingResolution: true,
                    }
                }

                const matchedOption =
                    findOption(raw) ||
                    findByLooseMatch(raw)

                if (matchedOption) {
                    return {
                        value: normalizeDoc(matchedOption.value),
                        inputValue: getLabel(matchedOption),
                        invalid: false,
                        pendingResolution: false,
                    }
                }

                return {
                    value: normalizeDoc(raw),
                    inputValue: raw,
                    invalid: true,
                    pendingResolution: false,
                }
            })
        },
        [findByLooseMatch, findOption, getLabel, options, updateState]
    )

    const syncValue = useCallback(
        (key, externalValue, externalLabel) => {
            updateState(key, () => {
                const normalized = normalizeDoc(externalValue)

                if (!normalized && !externalLabel) {
                    return defaultFieldState
                }

                const hasOptions = Boolean(options?.length)
                const matchedOption = hasOptions
                    ? findOption(normalized || externalLabel) ||
                        findByLooseMatch(normalized || externalLabel)
                    : null

                const resolvedValue = matchedOption
                    ? normalizeDoc(matchedOption.value)
                    : normalized

                const inputText = externalLabel ||
                    (matchedOption
                        ? getLabel(matchedOption)
                        : externalValue || "")

                if (!hasOptions && resolvedValue) {
                    return {
                        value: resolvedValue,
                        inputValue: inputText,
                        invalid: false,
                        pendingResolution: true,
                    }
                }

                const valid = Boolean(
                    matchedOption ||
                    (resolvedValue && isValid(externalValue || resolvedValue))
                )

                return {
                    value: resolvedValue,
                    inputValue: inputText,
                    invalid: resolvedValue ? !valid : false,
                    pendingResolution: false,
                }
            })
        },
        [
            findByLooseMatch,
            findOption,
            getLabel,
            isValid,
            options,
            updateState,
        ]
    )

    const meta = useMemo(
        () => ({
            options,
            loading,
            resolved: Boolean(options?.length),
            freeSolo: true,
            getOptionLabel: getLabel,
            normalizeValue: normalizeDoc,
            filterKeys: ["label", "value", "search"],
            normalizeFilter: normalizeCompanySearch,
            minCharsToFilter: 1,
            noOptionsText: (input) => input
                ? `Não cadastrado ${input}`
                : "Nenhum resultado encontrado",
        }),
        [getLabel, loading, options]
    )

    useEffect(() => {
        Object.entries(stateMap).forEach(([key, currentState]) => {
            updateState(key, () => {
                const raw = `${currentState.inputValue || currentState.value || ""}`.trim()
                if (!raw.length) return defaultFieldState

                const exactMatch = findOption(currentState.value || currentState.inputValue)

                if (exactMatch) {
                    return {
                        value: normalizeDoc(exactMatch.value),
                        inputValue: getLabel(exactMatch),
                        invalid: false,
                        pendingResolution: false,
                    }
                }

                if (!options?.length) {
                    if (currentState.pendingResolution) return {...currentState, invalid: false}
                    return currentState
                }

                if (currentState.pendingResolution) {
                    const resolvedMatch = findOption(raw) || findByLooseMatch(raw)

                    if (resolvedMatch) {
                        return {
                            value: normalizeDoc(resolvedMatch.value),
                            inputValue: getLabel(resolvedMatch),
                            invalid: false,
                            pendingResolution: false,
                        }
                    }

                    return {...currentState, invalid: true, pendingResolution: false}
                }

                return {...currentState, invalid: !isValid(raw), pendingResolution: false}
            })
        })
    }, [findByLooseMatch, findOption, getLabel, isValid, options, stateMap, updateState])

    const actions = useMemo(
        () => ({
            setInputValue: (key, inputValue) => {
                updateState(key, (currentState) => ({
                    ...currentState,
                    inputValue: inputValue || "",
                }))
            },

            setSelectedValue: (key, value) => {
                updateState(key, (currentState) => ({
                    ...currentState,
                    value: normalizeDoc(value),
                    invalid: false,
                }))
            },

            reset: () => setStateMap({}),
        }),
        [updateState]
    )

    const computed = useCallback(
        (key) => {
            const currentState = stateMap[key] || defaultFieldState

            return {
                hasError: Boolean(currentState.invalid),
                isLoading: Boolean(
                    loading ||
                    currentState.pendingResolution
                ),
                hasValue: Boolean(
                    currentState.value ||
                    currentState.inputValue
                ),
                value: currentState.value || "",
                inputValue: currentState.inputValue || "",
            }
        },
        [loading, stateMap]
    )

    const handlers = useMemo(
        () => ({
            onChange: handleChange,
            onInputChange: handleInputChange,
            onBlur: handleBlur,
            syncValue,
        }),
        [
            handleBlur,
            handleChange,
            handleInputChange,
            syncValue,
        ]
    )

    return useMemo(
        () => ({
            state: {byKey: stateMap},
            getState,
            actions,
            handlers,
            meta,
            computed,
            cleanup: actions.reset,
        }),
        [
            actions,
            computed,
            getState,
            handlers,
            meta,
            stateMap,
        ]
    )
}